import apiClient from '../client';
import type { AccessArea, AccessCell, AccessLevel, AccessMap } from '../../access/areas';

// The access matrix's API. The shared apiClient resolves to the body.

export interface AreaInfo { key: AccessArea; label: string; scoped: boolean }

export interface MatrixView {
  areas: AreaInfo[];
  levels: AccessLevel[];
  roles: string[];
  editableRoles: string[];
  defaults: Record<string, AccessMap>;
  overrides: Partial<Record<AccessArea, Record<string, AccessCell>>>;
  effective: Record<string, AccessMap>;
  updatedAt: string | null;
  yours: AccessMap;
  canEditAccessRow: boolean;
}

export interface PersonAccess {
  accountId: string;
  name: string;
  role: string;
  fromRole: AccessMap;
  exceptions: (AccessCell & { area: AccessArea })[];
  effective: AccessMap;
  editable: boolean;
  areas: AreaInfo[];
  levels: AccessLevel[];
}

/** One person's row in Settings → Access. */
export interface PersonRow {
  accountId: string;
  name: string;
  role: string;
  status: string;
  exceptions: (AccessCell & { area: AccessArea })[];
  effective: AccessMap;
  editable: boolean;
}

export function describeAccessError(err: any, fallback: string): string {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

const AccessService = {
  async me(): Promise<{ role: string; access: AccessMap }> {
    return apiClient.get('/api/access/me') as any;
  },
  async matrix(): Promise<MatrixView> {
    return apiClient.get('/api/access/matrix') as any;
  },
  async saveMatrix(overrides: MatrixView['overrides']): Promise<MatrixView & { changes: unknown[] }> {
    return apiClient.put('/api/access/matrix', { overrides }) as any;
  },
  async people(): Promise<PersonRow[]> {
    const body: { people: PersonRow[] } = await (apiClient.get('/api/access/people') as any);
    return body.people || [];
  },
  async person(accountId: string): Promise<PersonAccess> {
    return apiClient.get(`/api/access/people/${accountId}`) as any;
  },
  async savePerson(accountId: string, exceptions: PersonAccess['exceptions']): Promise<PersonAccess> {
    return apiClient.put(`/api/access/people/${accountId}`, { exceptions }) as any;
  },
};

export default AccessService;
